export interface PendingChangeBase {
  id: string;
  type: 'create' | 'update' | 'delete' | 'annotation';
}

export interface PendingCreateChange extends PendingChangeBase {
  type: 'create';
  data: {
    description: string;
    factDateText: string;
    docId: string;
  };
}

export interface PendingUpdateChange extends PendingChangeBase {
  type: 'update';
  data: {
    description?: string;
    factDateText?: string;
  };
}

export interface PendingDeleteChange extends PendingChangeBase {
  type: 'delete';
}

// New type for annotation changes (text, notes, arrows, highlights)
export interface PendingAnnotationChange extends PendingChangeBase {
  type: 'annotation';
  shapeType: 'text' | 'note' | 'arrow' | 'highlight' | 'geo' | 'other';
}

export type PendingChange = PendingCreateChange | PendingUpdateChange | PendingDeleteChange | PendingAnnotationChange;

// Class for tracking changes to a specific chronology visualisation
export class VisualisationPendingChanges {
  private changes: Map<string, PendingChange> = new Map();
  private listeners: Set<() => void> = new Set();

  // Add a listener for change events
  addListener(listener: () => void) {
    this.listeners.add(listener);
  }

  // Remove a listener
  removeListener(listener: () => void) {
    this.listeners.delete(listener);
  }

  // Notify all listeners of changes
  private notifyListeners() {
    this.listeners.forEach((listener) => listener());
  }

  // Add a pending create change
  addCreateChange(nodeId: string, data: PendingCreateChange['data']): string {
    const change: PendingCreateChange = {
      id: nodeId,
      type: 'create',
      data,
    };

    // Remove any existing changes for this node (there shouldn't be any, but just in case)
    this.removeChangesByNodeId(nodeId);
    this.changes.set(nodeId, change);
    this.notifyListeners();
    return nodeId;
  }

  // Add a pending update change
  addUpdateChange(nodeId: string, updates: PendingUpdateChange['data']): string {
    // Check if there's already an update for this fact and remove it
    const existingUpdate = Array.from(this.changes.values()).find(
      (change) => change.type === 'update' && change.id === nodeId,
    ) as PendingUpdateChange | undefined;

    if (existingUpdate) {
      this.changes.delete(existingUpdate.id);
    }

    this.changes.set(nodeId, {
      id: nodeId,
      type: 'update',
      data: updates,
    });
    this.notifyListeners();
    return nodeId;
  }

  // Add a pending delete change
  addDeleteChange(nodeId: string): string {
    // Remove any existing changes for this node as we're deleting it
    this.removeChangesByNodeId(nodeId);
    this.changes.set(nodeId, {
      id: nodeId,
      type: 'delete',
    });
    this.notifyListeners();
    return nodeId;
  }

  // Add a pending annotation change (for text, notes, arrows, highlights)
  addAnnotationChange(shapeId: string, shapeType: PendingAnnotationChange['shapeType']): string {
    const change: PendingAnnotationChange = {
      id: shapeId,
      type: 'annotation',
      shapeType,
    };

    this.changes.set(shapeId, change);
    this.notifyListeners();
    return shapeId;
  }

  // Remove a specific change
  removeChange(changeId: string): boolean {
    const removed = this.changes.delete(changeId);
    if (removed) {
      this.notifyListeners();
    }
    return removed;
  }

  // Remove all changes for a specific node
  removeChangesByNodeId(nodeId: string): void {
    let removed = false;
    for (const [id, change] of this.changes.entries()) {
      if (change.id === nodeId) {
        this.changes.delete(id);
        removed = true;
      }
    }
    if (removed) {
      this.notifyListeners();
    }
  }

  // Get all pending changes
  getAllChanges(): PendingChange[] {
    return Array.from(this.changes.values());
  }

  // Get change count
  getChangeCount(): number {
    return this.changes.size;
  }

  // Check if there are unsaved changes
  hasUnsavedChanges(): boolean {
    return this.changes.size > 0;
  }

  // Clear all pending changes
  clearAllChanges(): void {
    if (this.changes.size > 0) {
      this.changes.clear();
      this.notifyListeners();
    }
  }

  // Get a specific change by ID
  getChange(changeId: string): PendingChange | undefined {
    return this.changes.get(changeId);
  }

  // Dispose of the instance and clean up listeners
  dispose(): void {
    this.clearAllChanges();
    this.listeners.clear();
  }
}
